import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from './../../../environments/environment';
import { ownErrorToastOptions } from '../interceptors/http-error.interceptor';

import { PaginatedResult } from './../model/paginated-result';
import { AiAgent } from 'src/app/models/AiAgent';

/** Página de agentes com as contagens por segmento do cliente inteiro (M4). */
export interface AiAgentPage extends PaginatedResult<AiAgent[]> {
  segment_counts?: { all: number; wa: number; plat: number; list: number; off: number };
}

/** Uma mensagem do "Testar agente". O custo vem em R$ (string decimal), nunca em tokens. */
export interface AgentTestMessage {
  role: 'user' | 'assistant';
  content: string;
  cost_brl?: string | null;
  created_at?: string;
}

/** Resposta do teste: só o texto e quanto custou no saldo da carteira. */
export interface AgentTestReply {
  content: string;
  cost_brl: string | null;
}

/** Rascunho testado no formulário curto antes de criar o agente (ou a cópia). */
export interface AgentDraft {
  name?: string;
  base_prompt: string;
  smsfunnel_ai_model_id: string;
  /** Na cópia: o agente de origem (chaves, ofertas). */
  source_agent_id?: string | null;
  /** Gerado por formulário aberto; liga o teste ao agente quando ele é criado. */
  draft_key: string;
}

/** Passo de WhatsApp em que o agente é o "Responder com IA". */
export interface AiAgentSequence {
  id: string;
  campaign_id: string;
  campaign_name: string;
  template_name: string | null;
  active: boolean;
  meta_whatsapp_account_id: string | null;
  whatsapp_account_name: string | null;
}

@Injectable({ providedIn: 'root' })
export class AiAgentService {

  constructor(private http: HttpClient) { }

  /**
   * Filtros da tela de agentes: nome, número de WhatsApp (`'none'` = sem número) e segmento da
   * tela setorizada (`channel`: wa | plat | list | off). Os filtros por integração e lista saíram
   * da tela na M4 (viraram o segmento e os selos). `withUnassigned` junta ao número os agentes
   * sem número, que respondem por qualquer um (a escolha do passo de WhatsApp); `withCounts` pede
   * as contagens dos segmentos.
   */
  listPaginated(
    page: number,
    pageSize: number,
    filters: { name?: string; whatsappAccountId?: string; channel?: string; withUnassigned?: boolean; withCounts?: boolean } = {}
  ): Observable<AiAgentPage> {
    let url = `${environment.apiUrl}/ai-agents?page=${page}&per_page=${pageSize}`;
    if (filters.name) {
      url += `&name=${encodeURIComponent(filters.name)}`;
    }
    if (filters.whatsappAccountId) {
      url += `&whatsapp_account_id=${encodeURIComponent(filters.whatsappAccountId)}`;
    }
    if (filters.channel) {
      url += `&channel=${encodeURIComponent(filters.channel)}`;
    }
    if (filters.withUnassigned) {
      url += '&with_unassigned=1';
    }
    // As pílulas dos segmentos (`segment_counts`) só vêm quando a tela setorizada pede.
    if (filters.withCounts) {
      url += '&with_counts=1';
    }
    return this.http.get<AiAgentPage>(url);
  }

  /** Passos de WhatsApp em que o agente responde, de qualquer número ("Onde este agente responde"). */
  listSequences(id: string): Observable<AiAgentSequence[]> {
    return this.http.get<AiAgentSequence[]>(`${environment.apiUrl}/ai-agents/${id}/sequences`);
  }

  findById(id: string): Observable<AiAgent> {
    return this.http.get<AiAgent>(`${environment.apiUrl}/ai-agents/${id}`);
  }

  save(agent: Partial<AiAgent>): Observable<AiAgent> {
    if (agent.id) {
      return this.update(agent.id, agent);
    }
    return this.create(agent);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/ai-agents/${id}`);
  }

  /**
   * "Copiar agente": o formulário abre com os dados do original e só chama aqui ao confirmar, com
   * o que o cliente editou (CU-17tm466mg5z). Eventos e ofertas vêm do original no backend.
   */
  duplicate(id: string, copy: Partial<AiAgent> & { test_draft_key?: string } = {}): Observable<AiAgent> {
    return this.http.post<AiAgent>(`${environment.apiUrl}/ai-agents/${id}/duplicate`, copy, ownErrorToastOptions());
  }

  test(id: string, message: string, conversationHistory: { role: 'user' | 'assistant'; content: string }[] = []): Observable<AgentTestReply> {
    return this.http.post<AgentTestReply>(`${environment.apiUrl}/ai-agents/${id}/test?no-loading=1`, {
      message,
      conversation_history: conversationHistory,
    });
  }

  /** Teste do agente ainda não criado (formulário curto do passo de WhatsApp). */
  testDraft(draft: AgentDraft, message: string, conversationHistory: { role: 'user' | 'assistant'; content: string }[] = []): Observable<AgentTestReply> {
    return this.http.post<AgentTestReply>(`${environment.apiUrl}/ai-agents/test-draft?no-loading=1`, {
      ...draft,
      message,
      conversation_history: conversationHistory,
    });
  }

  /** Conversas de teste dos últimos 7 dias com o agente. */
  testHistory(id: string): Observable<{ data: AgentTestMessage[] }> {
    return this.http.get<{ data: AgentTestMessage[] }>(`${environment.apiUrl}/ai-agents/${id}/test-history?no-loading=1`);
  }

  clearTestHistory(id: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/ai-agents/${id}/test-history?no-loading=1`);
  }

  /** Quanto UMA resposta custa no saldo da carteira, em R$ (string decimal; nulo = sem estimativa). */
  costEstimate(params: { smsfunnelAiModelId?: string | null; provider?: string; model?: string; promptChars: number }): Observable<{ estimate_brl: string | null }> {
    const query: string[] = [`prompt_chars=${Math.max(0, Math.round(params.promptChars || 0))}`, 'no-loading=1'];
    if (params.smsfunnelAiModelId) {
      query.push(`smsfunnel_ai_model_id=${encodeURIComponent(params.smsfunnelAiModelId)}`);
    }
    if (params.provider) {
      query.push(`provider=${encodeURIComponent(params.provider)}`);
    }
    if (params.model) {
      query.push(`model=${encodeURIComponent(params.model)}`);
    }
    return this.http.get<{ estimate_brl: string | null }>(`${environment.apiUrl}/ai-agents/cost-estimate?${query.join('&')}`);
  }

  // A tela do agente dá o próprio aviso do erro (o do número no campo, CU-17tm466mfzu): sem o
  // toast do interceptor, que repetia o 422 em cima do dela.
  private create(agent: Partial<AiAgent>): Observable<AiAgent> {
    return this.http.post<AiAgent>(`${environment.apiUrl}/ai-agents`, agent, ownErrorToastOptions());
  }

  private update(id: string, agent: Partial<AiAgent>): Observable<AiAgent> {
    return this.http.put<AiAgent>(`${environment.apiUrl}/ai-agents/${id}`, agent, ownErrorToastOptions());
  }
}
